import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl, isNotEmpty, numericOperators, dateOperators, stringifyArrayOfItems, isEmpty } from '../../lib/ep.js';
import { stage_promotion_types } from '../../data/bootstrap_stages.js';
import { useQuery } from 'react-query';

import ActionLink from '../builder/ActionLink.js';
import Checkbox from '../builder/Checkbox.js';
import CheckEnabler from '../builder/CheckEnabler.js';
import DateTimePicker from '../builder/DateTimePicker.js';
import DynamicFields from '../builder/DynamicFields.js';
import Input from '../builder/Input';
import Model from '../builder/Model';
import Select from '../builder/Select';
import TextArea from '../builder/TextArea.js';
import ToggleCollection from '../builder/ToggleCollection.js';
import Tooltip from '../builder/Tooltip.js';

import { get_all_validators } from '../../lib/tag_validators.js';

const TicketConditions = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m,props.intl, c);

	const type = model.type;
	let match = model?.match ?? [];

	const type_tags = props.tags.filter((t) => (t.OcrTag.type == type));

	const selectable_tags = [];
	type_tags.map((tag) => {
		selectable_tags.push({
			id:tag.OcrTag.id,
			title:tag.OcrTag.name,
			autotranslate:false,
		});
	});

	const pickTag = (tag_id) => {
		if(match.includes(tag_id)) {
			match = match.filter((t) => (t != tag_id));
		} else {
			match.push(tag_id);
		}

		change('match', match);
	};

	const can_have_extra_participations = [
		stage_promotion_types.ROULETTE.id,
		stage_promotion_types.SCRATCH_WIN.id,
		stage_promotion_types.REVEAL_WIN.id,
		stage_promotion_types.STAMP_CARD.id
	].includes(parseInt(props.stage.promotion_type_id));

	const selected = match ?? [];

	let available_conditions = [{ k:'', v:`-- ${i18n("Choose operator")} --` }];

	if(['ticket_dates'].includes(type)) {
		return (
			<div>
				<Model
					model={model}
					change={change}
				>
					<Select
						name='operator'
						values={[
							...available_conditions,
							...dateOperators,
						]}
						_class={{ size:70 }}
					>Operator</Select>

					{((model?.operator ?? '') != '') && (
						<DateTimePicker
							name='value'
							standalone={true}
							_class={{ size:70 }}
						>Value</DateTimePicker>
					)}

					{((model?.operator ?? '') != '' && model.operator == 'BETWEEN') && (
						<DateTimePicker
							name='value_end'
							standalone={true}
							_class={{ size:70 }}
						>Value end</DateTimePicker>
					)}
				</Model>
			</div>
		)
	}

	if(['ticket_total'/*, 'amount_article', 'quantity_article'*/].includes(type)) {
		const tags = props.tags.filter((t) => (t.OcrTag.type == 'article')).map((tag) => ({
			id:tag.OcrTag.id,
			title:tag.OcrTag.name,
			autotranslate:false,
		}));

		const display_tag_selector = (!['ticket_total'].includes(type)) ? true : false;
		const has_selected_matched = ((model?.match ?? []).length > 0) ? true : false;
		const has_selected_operator = ((model?.operator ?? '') != '') ? true : false;

		return (
			<div>
				<Model
					model={model}
					change={change}
				>
					{(display_tag_selector) && (
						<ToggleCollection
							items={tags}
							onToggle={pickTag}
							selected={selected}
							icon={false}
							color='green'
							min={1}
						>Select fields</ToggleCollection>
					)}

					{(!display_tag_selector || has_selected_matched) && (
						<Select
							name='operator'
							values={[
								...available_conditions,
								...numericOperators,
							]}
							_class={{ size:70 }}
						>Operator</Select>
					)}

					{((!display_tag_selector || has_selected_matched) && has_selected_operator) && (
						<Input
							name='value'
							_type='number'
							_class={{ size:70 }}
						>Value</Input>
					)}

					{(can_have_extra_participations && (!display_tag_selector || has_selected_matched) && has_selected_operator) && (
						<Input
							name='ticket_every_total'
							_type='number'
							_class={{ size:50 }}
							label_props={{
								tooltip:<Tooltip>Enter the amount the user must spend to receive one entry. For example, if you enter 20 and the user spends 42, they will receive 2 entries.</Tooltip>,
							}}
						>One entry for every X spent</Input>
					)}
				</Model>
			</div>
		)
	}

	if(['article'].includes(type)) {
		return (
			<div>
				<ToggleCollection
					items={selectable_tags}
					onToggle={pickTag}
					selected={selected}
					icon={false}
					color='green'
					min={1}
				>Select fields</ToggleCollection>

				{(selected.length > 0) && (
					<Model
						model={model}
						change={change}
					>
						<CheckEnabler
							label="Check minimum quantity?"
							enabled={(parseInt(model?.min_quantity ?? '0') > 0) ? true : false}
							onDisable={() => {
								change('min_quantity', "")
								change('ticket_every_qty', "")
							}}
							onEnable={() => change('min_quantity', 1)}
							label_props={{
								tooltip:<Tooltip>In case the receipt has multiple units of an article, this amount corresponds to the sum of all amounts spent on articles that match the tag</Tooltip>,
							}}
						>
							<Input
								model={model}
								change={change}
								_type='number'
								min={0}
								name="min_quantity"
								_class={{ size:40 }}
							>Minimum quantity</Input>

							<Checkbox
								model={model}
								change={change}
								name="ticket_every_qty"
							>Give extra participations for each minimum quantity</Checkbox>
						</CheckEnabler>

						<CheckEnabler
							label="Check minimum amount?"
							enabled={(parseInt(model?.min_amount ?? '0') > 0) ? true : false}
							onDisable={() => {
								change('min_amount', "")
								change('ticket_every_amount', "")
							}}
							onEnable={() => change('min_amount', 1)}
							label_props={{
								tooltip:<Tooltip>Total sum of the quantities of items that match the tag</Tooltip>,
							}}
						>
							<Input
								model={model}
								change={change}
								_type='number'
								min={0}
								name="min_amount"
								_class={{ size:40 }}
							>Minimum amount</Input>

							<Checkbox
								model={model}
								change={change}
								name="ticket_every_amount"
							>Give extra participations for each minimum amount</Checkbox>
						</CheckEnabler>
					</Model>
				)}
			</div>
		)
	}

	return (
		<div>
			<ToggleCollection
				items={selectable_tags}
				onToggle={pickTag}
				selected={selected}
				icon={false}
				color='green'
				min={1}
			>Select fields</ToggleCollection>
			
			{(type == 'list') && (
				<Model
					model={model}
					change={change}
				>
					<TextArea
						name="matches"
					>Values</TextArea>
				</Model>
			)}
		</div>
	)
};

export const GetRuleTitle = (type) => {
	switch(type) {
		case 'store':
			return "Tag Store";

		case 'location':
			return "Tag Location";

		case 'article':
			return "Tag Article";

		case 'quantity_article':
			return "Article quantity";

		case 'amount_article':
			return "Article amount";

		case 'ticket_total':
			return "Receipt total amount";

		case 'ticket_dates':
			return "Receipt date";
		
		default:
			return "Unknown Rule"
	}
};

export const GetRuleSubtitle = (type, item, tags, intl, version = 'all') => {
	const i18n = (m, c) => i18nIntl(m, intl, c);

	const humanizeQuantityOperator = (operator) => {
		const op = numericOperators.find((o) => (o.k == operator));
		switch(op.k) {
			case '<':
				return "***Less than";	// "***" to separate from "Less than" (in spanish "menor que", and here needs to be "menos que")

			case '<=':
				return "At much";
	
			case '>':
				return "***More than";	// "***" to separate from "More than" (in spanish "mayor que", and here needs to be "más que")

			case '>=':
				return "At least";
		}

		return op?.v ?? k;
	};

	const humanizeDateOperator = (operator) => {
		const joined_operators = [...numericOperators, ...dateOperators];
		const op = joined_operators.find((o) => (o.k == operator));
		return op?.v ?? k;
	};

	const humanizeTagsList = (value) => {
		const matches = [];
		value.map((tag_id) => {
			const tag_matches = tags?.find((t) => (t.OcrTag.id == tag_id));
			if(tag_matches == undefined) return;
			matches.push(tag_matches.OcrTag.name);
		});

		return stringifyArrayOfItems(matches, " or ");
	};

	switch(type) {
		case 'store':
		case 'location':
		case 'article':
			if(isEmpty(item, 'match')) return null;
			if(item.match.length == 0) return null;

			let min_quantity = "";
			let min_amount = "";
			if(type == 'article') {
				if(isNotEmpty(item, 'min_quantity')) {
					min_quantity = intl.formatMessage({ id:"At least {num} item(s)" }, { num:parseInt(item.min_quantity) });

					if(version == 'all') min_quantity = ` (${min_quantity})`;
				}

				if(isNotEmpty(item, 'min_amount')) {
					min_amount = intl.formatMessage({ id:"Spent at least {num}" }, { num:parseInt(item.min_amount) });

					if(version == 'all') min_amount = ` (${min_amount})`;
				}
			}

			if(version == 'conditions') {
				return `${min_quantity}${min_amount}`;
			}

			if(version == 'match') {
				return `${humanizeTagsList(item.match)}`;
			}

			return `${humanizeTagsList(item.match)}${min_quantity}${min_amount}`;

		case 'ticket_total':
			if(isEmpty(item, 'value')) return null;
			return intl.formatMessage({
				id:"Spent {operator} {value}",
			}, {
				operator:i18n(humanizeQuantityOperator(item.operator), 'lowercase'),
				value:item.value,
			});

		case 'ticket_dates':
			if(isEmpty(item, 'value') && isEmpty(item, 'value_end')) return null;
			if(item.operator != 'BETWEEN') {
				return intl.formatMessage({
					id:"{operator} {value}",
				}, {
					operator:i18n(humanizeDateOperator(item.operator)),
					value:EPDateFormat(item?.value, 'L'),
				});
			} else {
				return intl.formatMessage({
					id:"Between {value1} and {value2}",
				}, {
					value1:EPDateFormat(item?.value, 'L'),
					value2:EPDateFormat(item?.value_end, 'L'),
				});
			}
	}
};

export const validateOcrTicketConditions = (conditions) => {
	let is_valid = true;
	const valid_conditions = [];
	conditions.map(condition=>{
		let is_current_valid = true;
		switch (condition.type) {
			case 'store':
			case 'location':
			case 'article':
				if (condition?.match == undefined || condition?.match.length == 0) {
					is_current_valid = false;
				}
				break;
			case 'ticket_dates':
			case 'ticket_total':
				if (isEmpty(condition,'operator') || isEmpty(condition,'value')) {
					is_current_valid = false
				}
			default:
				break;
		}
		if (!is_current_valid) {
			is_valid = false;
			return;
		}
		valid_conditions.push(condition);

	})
	return {is_valid,valid_conditions}
}

const OcrTicketSidebar = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const { loading, data:tags } = useQuery('tag_validators', () => get_all_validators(props.promotion.account_id), { refetchOnWindowFocus:true });

	const conditions = stage?.reqs_ocr_conditions ?? [];
	const [has_errors, setHasErrors] = useState(false);

	useEffect(() => {
		if(stage.reqs_ocr_verify_amount) return;

		const conditions_without_amount = conditions.filter((c) => (c.k != 'ticket_amount'));
		change('reqs_ocr_conditions', [ ...conditions_without_amount ]);
	}, [ stage.reqs_ocr_verify_amount ]);

	useEffect(() => {
		if(stage.reqs_ocr_date_format) return;
		const conditions_without_date = conditions.filter((c) => (c.k != 'date'));
		change('reqs_ocr_conditions', [ ...conditions_without_date ]);
	}, [ stage.reqs_ocr_date_format ]);

	const dynamic_fields = [{
		type:'ticket_total',
		label:"Receipt total amount",
		icon:'knk-buy-ticket',
		title:(f) => {
			return i18n(GetRuleTitle('ticket_total'));
		},
		subtitle:(f) => {
			return GetRuleSubtitle('ticket_total', f, (tags || []), props.intl);
		},
	}, {
		type:'ticket_dates',
		label:"Receipt date",
		icon:'knk-calendar',
		title:(f) => {
			return i18n(GetRuleTitle('ticket_dates'));
		},
		subtitle:(f) => {
			return GetRuleSubtitle('ticket_dates', f, (tags || []), props.intl);
		},
	}, {
		type:'store',
		label:"Receipt Tag Store",
		icon:'knk-get-users-to-pos',
		max:1,
		title:(f) => {
			return i18n(GetRuleTitle('store'));
		},
		subtitle:(f) => {
			return GetRuleSubtitle('store', f, (tags || []), props.intl);
		},
	}, {
		type:'location',
		label:"Receipt Tag Location",
		icon:'knk-city',
		max:1,
		title:(f) => {
			return i18n(GetRuleTitle('location'));
		},
		subtitle:(f) => {
			return GetRuleSubtitle('location', f, (tags || []), props.intl);
		},
	}, {
		type:'article',
		label:"Receipt Tag Article",
		icon:'knk-present-fill',
		title:(f) => {
			return i18n(GetRuleTitle('article'));
		},
		subtitle:(f) => {
			return GetRuleSubtitle('article', f, (tags || []), props.intl);
		},
	}];


	const validate_conditions = (conditions) => {
		const {is_valid} = validateOcrTicketConditions(conditions);
		if (!is_valid) {
			setHasErrors(true);
			return;
		}
		setHasErrors(false);
		change('reqs_ocrticket_conditions', conditions)
	}

	return (
		<>
			{(window?.is_admin ?? false) && (
				<Model
					model={stage}
					change={change}
				>
					<TextArea
						name='reqs_extra_prompt'
						_class={{ first:true, other:'not_in_bottom' }}
					>Extra prompt</TextArea>
				</Model>
			)}

			<DynamicFields
				name="reqs_ocrticket_conditions"
				section_title="Conditions"
				section_icon='knk-filter'
				fields={stage?.reqs_ocrticket_conditions ?? []}
				field_types={dynamic_fields}
				onChange={validate_conditions}
				sortable={false}
				can_copy={false}
				add_field_label="Add condition"
				add_button_props={{
					icon:'knk-add-item mainButton',
				}}
				extra_message={<>
					<p>
						<ActionLink
							_href={`${window.zendesk_base_url}34968848348308`}
							_target='_blank'
							_class={{ other:'gui_not_in_start_tiny' }}
						>Learn how conditions work</ActionLink>
					</p>

					<p class="gui_not_in_top_tiny">
						<ActionLink
							_href='/tag_validators'
							_target='_blank'
							_class={{ other:'gui_not_in_start_tiny' }}
						>Manage your Receipt Tags</ActionLink>
					</p>
				</>}
			>
				<TicketConditions
					tags={tags}
					stage={stage}
					intl={props.intl}
				/>
			</DynamicFields>

			{(has_errors) && (
				<p class="gui_not_in_top_tiny redColor">
					{i18n("The conditions are not valid and will not be saved.")}
				</p>
			)}
		</>
	);
}

export default injectIntl(OcrTicketSidebar);